import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Table, Button, Space, Tag, message, Popconfirm, Input } from 'antd';
import { PlusOutlined, UploadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { CreateTournamentModal } from '../CreateTournamentModal';
import { EditTournamentModal } from '../EditTournamentModal';
import apiService from '@/services/api.service';
import type { Tournament } from '@/types/models';
import type { TournamentStatus } from '@/types/models/tournament';
import type { ApiResponse } from '@/types/api';
import { logger } from '@/utils/logger';
import { formatDate } from '@/utils/date';
import { API_ENDPOINTS } from '@/constants/api.constants';
import { useAuth } from '@/contexts/AuthContext';

const TournamentsPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const [tournaments, setTournaments] = useState<Tournament[]>([]);
    const [isTournamentsLoading, setIsTournamentsLoading] = useState(false);
    const [isCreateTournamentModalVisible, setIsCreateTournamentModalVisible] = useState(false);
    const [isEditTournamentModalVisible, setIsEditTournamentModalVisible] = useState(false);
    const [selectedTournament, setSelectedTournament] = useState<Tournament | null>(null);
    const [checkingLicense, setCheckingLicense] = useState(false);

    const fetchTournaments = async () => {
        try {
            setIsTournamentsLoading(true);
            const response = await apiService.get<ApiResponse<Tournament[]>>(
                API_ENDPOINTS.TOURNAMENTS.BASE,
                { params: { limit: 1000 } }
            );
            setTournaments(Array.isArray(response.data.data) ? response.data.data : []);
        } catch (error) {
            logger.error('Error fetching tournaments:', error);
            message.error('Ошибка при загрузке списка турниров');
            setTournaments([]);
        } finally {
            setIsTournamentsLoading(false);
        }
    };

    const handleCreateTournament = async () => {
        if (user?.role === 'admin') {
            setIsCreateTournamentModalVisible(true);
            return;
        }
        try {
            setCheckingLicense(true);
            const res = await apiService.get<ApiResponse<{ canCreateTournament: boolean; extendedLicensePrice?: number }>>(
                API_ENDPOINTS.PAYMENTS.EXTENDED_LICENSE_INFO
            );
            const data = res.data?.data;
            if (data && !data.canCreateTournament) {
                navigate('/extended-license', { state: { fromCreate: true, extendedLicensePrice: data.extendedLicensePrice } });
                return;
            }
            setIsCreateTournamentModalVisible(true);
        } catch (e) {
            logger.error('Error checking extended license:', e);
            message.error('Не удалось проверить возможность создания турнира');
        } finally {
            setCheckingLicense(false);
        }
    };

    const handleTournamentCreateSuccess = () => {
        setIsCreateTournamentModalVisible(false);
        fetchTournaments();
    };

    const handleTournamentEditSuccess = () => {
        setIsEditTournamentModalVisible(false);
        setSelectedTournament(null);
        fetchTournaments();
    };

    const handleDeleteTournament = async (id: number) => {
        try {
            await apiService.delete(`${API_ENDPOINTS.TOURNAMENTS.BASE}/${id}`);
            message.success('Турнир успешно удален');
            fetchTournaments();
        } catch (error) {
            logger.error('Error deleting tournament:', error);
            message.error('Ошибка при удалении турнира');
        }
    };

    // Загружаем и обновляем список при открытии/переходе на страницу
    useEffect(() => {
        if (location.pathname === '/admin/tournaments') {
            fetchTournaments();
        }
    }, [location.pathname]);

    const tournamentColumns: ColumnsType<Tournament> = [
        {
            title: 'Название',
            dataIndex: 'name',
            key: 'name',
            sorter: (a, b) => a.name.localeCompare(b.name),
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                <div style={{ padding: 8 }}>
                    <Input
                        style={{ width: 188, marginBottom: 8, display: 'block' }}
                        placeholder="Поиск по названию"
                        value={selectedKeys[0]}
                        onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => confirm()}
                    />
                    <Space>
                        <Button type="primary" onClick={() => confirm()} size="small" style={{ width: 90 }}>
                            Поиск
                        </Button>
                        <Button onClick={() => clearFilters && clearFilters()} size="small" style={{ width: 90 }}>
                            Сброс
                        </Button>
                    </Space>
                </div>
            ),
            onFilter: (value, record) =>
                record.name.toLowerCase().includes(String(value).toLowerCase()),
        },
        {
            title: 'Место проведения',
            dataIndex: 'location',
            key: 'location',
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                <div style={{ padding: 8 }}>
                    <Input
                        style={{ width: 188, marginBottom: 8, display: 'block' }}
                        placeholder="Поиск по месту"
                        value={selectedKeys[0]}
                        onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => confirm()}
                    />
                    <Space>
                        <Button type="primary" onClick={() => confirm()} size="small" style={{ width: 90 }}>
                            Поиск
                        </Button>
                        <Button onClick={() => clearFilters && clearFilters()} size="small" style={{ width: 90 }}>
                            Сброс
                        </Button>
                    </Space>
                </div>
            ),
            onFilter: (value, record) =>
                record.location?.toLowerCase().includes(String(value).toLowerCase()),
        },
        {
            title: 'Дата проведения',
            dataIndex: 'tournamentDate',
            key: 'tournamentDate',
            render: (date: string) => formatDate(date),
            sorter: (a, b) => {
                const dateA = new Date(a.tournamentDate || 0).getTime();
                const dateB = new Date(b.tournamentDate || 0).getTime();
                return dateA - dateB;
            },
        },
        {
            title: 'Статус',
            dataIndex: 'status',
            key: 'status',
            filters: [
                { text: 'Черновик', value: 'draft' },
                { text: 'Опубликован', value: 'published' },
                { text: 'В процессе', value: 'in_progress' },
                { text: 'Завершен', value: 'completed' },
                { text: 'Удален', value: 'deleted' },
            ],
            onFilter: (value, record) => record.status === value,
            render: (status: TournamentStatus) => {
                const statusConfig: Record<string, { color: string; text: string }> = {
                    draft: { color: 'default', text: 'Черновик' },
                    published: { color: 'blue', text: 'Опубликован' },
                    in_progress: { color: 'green', text: 'В процессе' },
                    completed: { color: 'success', text: 'Завершен' },
                    deleted: { color: 'red', text: 'Удален' }
                };
                const config = statusConfig[status] || { color: 'default', text: status };
                return <Tag color={config.color}>{config.text}</Tag>;
            },
        },
        {
            title: 'Участников',
            dataIndex: 'participantsCount',
            key: 'participantsCount',
            render: (count: number) => count || 0,
            sorter: (a, b) => (a.participantsCount || 0) - (b.participantsCount || 0),
        },
        {
            title: 'Два 3 места',
            dataIndex: 'hasTwoThirdPlaces',
            key: 'hasTwoThirdPlaces',
            render: (value: boolean) => value ? <Tag color="blue">Да</Tag> : <Tag>Нет</Tag>,
            filters: [
                { text: 'Да', value: true },
                { text: 'Нет', value: false },
            ],
            onFilter: (value, record) => record.hasTwoThirdPlaces === value,
        },
        {
            title: 'Действия',
            key: 'actions',
            render: (_: unknown, record: Tournament) => (
                <Space>
                    <Button type="link" onClick={() => {
                        setSelectedTournament(record);
                        setIsEditTournamentModalVisible(true);
                    }}>
                        Редактировать
                    </Button>
                    <Popconfirm
                        title="Удалить турнир?"
                        description="Это действие нельзя отменить"
                        onConfirm={() => handleDeleteTournament(record.id)}
                        okText="Да"
                        cancelText="Нет"
                    >
                        <Button type="link" danger>
                            Удалить
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <div style={{ marginBottom: 16 }}>
                <Space>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleCreateTournament}
                        loading={checkingLicense}
                    >
                        Создать турнир
                    </Button>
                    <Button
                        icon={<UploadOutlined />}
                        onClick={() => {/* TODO: Implement import */}}
                    >
                        Импорт
                    </Button>
                </Space>
            </div>
            <Table
                columns={tournamentColumns}
                dataSource={tournaments}
                rowKey="id"
                loading={isTournamentsLoading}
                pagination={{
                    showSizeChanger: true,
                    showTotal: (total) => `Всего ${total} турниров`,
                }}
            />
            <CreateTournamentModal
                visible={isCreateTournamentModalVisible}
                onCancel={() => setIsCreateTournamentModalVisible(false)}
                onSuccess={handleTournamentCreateSuccess}
            />
            <EditTournamentModal
                visible={isEditTournamentModalVisible}
                tournament={selectedTournament}
                onCancel={() => {
                    setIsEditTournamentModalVisible(false);
                    setSelectedTournament(null);
                }}
                onSuccess={handleTournamentEditSuccess}
            />
        </div>
    );
};

export default TournamentsPage;


